"use client";

import { useState } from "react";

/**
 * Date-of-birth picker, ported from the source's `ui/native-calendar`.
 *
 * The source builds the grid by hand rather than using react-day-picker, so the
 * port needs no new dependency. Its month/year dropdowns are shadcn `Select`s
 * styled `border-0 shadow-none`; native `<select>`s are used here instead —
 * visually equivalent once chrome is removed, and they avoid nesting a Radix
 * Select inside a popover inside a dialog, which is a well-known focus trap.
 */

interface NativeCalendarProps {
  selected?: Date;
  onSelect: (date: Date) => void;
  /** Return true to block a day. The source blocks the future and pre-1940. */
  disabled?: (date: Date) => boolean;
  className?: string;
}

const MONTH_NAMES = [
  "January",
  "February",
  "March",
  "April",
  "May",
  "June",
  "July",
  "August",
  "September",
  "October",
  "November",
  "December",
];

const DAY_NAMES = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

const FIRST_YEAR = 1940;

const daysInMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth() + 1, 0).getDate();

const firstWeekdayOfMonth = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), 1).getDay();

const isSameDay = (a: Date, b: Date) =>
  a.getDate() === b.getDate() &&
  a.getMonth() === b.getMonth() &&
  a.getFullYear() === b.getFullYear();

/** Matches the source's `w-auto h-8 text-sm border-0 shadow-none` triggers. */
const SELECT_CLASSNAME =
  "h-8 w-auto rounded-md border-0 bg-transparent px-1 text-sm shadow-none " +
  "focus:outline-none focus:ring-1 focus:ring-slate-950";

export default function NativeCalendar({
  selected,
  onSelect,
  disabled,
  className = "",
}: NativeCalendarProps) {
  const [currentDate, setCurrentDate] = useState<Date>(selected || new Date());

  // 1940 → this year + 10, as in the source.
  const currentYear = new Date().getFullYear();
  const years = Array.from(
    { length: currentYear - FIRST_YEAR + 11 },
    (_, i) => FIRST_YEAR + i
  );

  const goToMonth = (delta: number) =>
    setCurrentDate(
      new Date(currentDate.getFullYear(), currentDate.getMonth() + delta, 1)
    );

  const handleDateClick = (day: number) => {
    const clicked = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      day
    );
    if (disabled?.(clicked)) return;
    onSelect(clicked);
  };

  const renderDays = () => {
    const total = daysInMonth(currentDate);
    const firstDay = firstWeekdayOfMonth(currentDate);
    const cells = [];

    // Trailing days of the previous month — shown greyed and inert.
    const prevMonthLastDay = new Date(
      currentDate.getFullYear(),
      currentDate.getMonth(),
      0
    ).getDate();

    for (let i = firstDay - 1; i >= 0; i--) {
      const day = prevMonthLastDay - i;
      cells.push(
        <button
          key={`prev-${day}`}
          type="button"
          className="w-8 h-8 text-gray-300 text-sm rounded flex items-center justify-center"
          disabled
        >
          {day}
        </button>
      );
    }

    for (let day = 1; day <= total; day++) {
      const date = new Date(
        currentDate.getFullYear(),
        currentDate.getMonth(),
        day
      );
      const isSelected = Boolean(selected && isSameDay(date, selected));
      const isDisabled = Boolean(disabled?.(date));

      cells.push(
        <button
          key={day}
          type="button"
          onClick={() => handleDateClick(day)}
          disabled={isDisabled}
          className={[
            "w-8 h-8 text-sm rounded flex items-center justify-center transition-colors",
            isSelected
              ? "bg-red-600 text-white font-semibold"
              : "text-gray-700 hover:bg-gray-100",
            isDisabled ? "text-gray-300 cursor-not-allowed" : "",
          ]
            .filter(Boolean)
            .join(" ")}
        >
          {day}
        </button>
      );
    }

    // Pad the final week so the grid stays rectangular.
    const totalCells = Math.ceil((firstDay + total) / 7) * 7;
    for (let day = 1; day <= totalCells - (firstDay + total); day++) {
      cells.push(
        <button
          key={`next-${day}`}
          type="button"
          className="w-8 h-8 text-gray-300 text-sm rounded flex items-center justify-center"
          disabled
        >
          {day}
        </button>
      );
    }

    return cells;
  };

  return (
    <div
      className={`bg-[#FFFFFF] border border-gray-200 rounded-lg shadow-lg p-4 w-80 ${className}`.trim()}
    >
      <div className="flex items-center justify-between mb-4">
        <button
          type="button"
          onClick={() => goToMonth(-1)}
          aria-label="Previous month"
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          {/* lucide ChevronLeft, inlined */}
          <svg
            className="h-4 w-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="m15 18-6-6 6-6" />
          </svg>
        </button>

        <div className="flex items-center gap-2">
          <select
            aria-label="Month"
            className={SELECT_CLASSNAME}
            value={currentDate.getMonth()}
            onChange={(e) =>
              setCurrentDate(
                new Date(currentDate.getFullYear(), Number(e.target.value), 1)
              )
            }
          >
            {MONTH_NAMES.map((month, index) => (
              <option key={month} value={index}>
                {month}
              </option>
            ))}
          </select>

          <select
            aria-label="Year"
            className={SELECT_CLASSNAME}
            value={currentDate.getFullYear()}
            onChange={(e) =>
              setCurrentDate(
                new Date(Number(e.target.value), currentDate.getMonth(), 1)
              )
            }
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>
        </div>

        <button
          type="button"
          onClick={() => goToMonth(1)}
          aria-label="Next month"
          className="p-1 hover:bg-gray-100 rounded transition-colors"
        >
          {/* lucide ChevronRight, inlined */}
          <svg
            className="h-4 w-4 text-gray-600"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path d="m9 18 6-6-6-6" />
          </svg>
        </button>
      </div>

      <div className="grid grid-cols-7 gap-1 mb-2">
        {DAY_NAMES.map((day) => (
          <div
            key={day}
            className="text-center text-sm font-medium text-gray-600 py-2"
          >
            {day}
          </div>
        ))}
      </div>

      <div className="grid grid-cols-7 gap-1">{renderDays()}</div>
    </div>
  );
}
